import React, { useState, useCallback, useEffect, useRef } from "react";
import { Wrapper } from "./styles";

interface IHeaderObserverProps {
    children: React.ReactNode;
    topOffset?: number;
    scrollContainer?: HTMLElement | null;
}

const DEFAULT_OFFSET = 10;
const SCROLL_DIRECTION_THRESHOLD_PX = 8;
const NEAR_BOTTOM_PX = 48;
const DELIBERATE_SCROLL_UP_AT_BOTTOM_PX = 20;

const HeaderObserver = ({ children, topOffset = DEFAULT_OFFSET, scrollContainer = null }: IHeaderObserverProps) => {
    const [hidden, setHidden] = useState(false);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const prevScroll = useRef(0);
    const scrollContainerRef = useRef<HTMLElement | null>(null);
    scrollContainerRef.current = scrollContainer;
    const rafRef = useRef<number | null>(null);
    const isHiddenRef = useRef(false);

    const updateContentMargin = useCallback((show: boolean, headerHeight: number) => {
        const contentEl = scrollContainerRef.current;
        if (!contentEl) return;
        contentEl.style.transition = "none";
        contentEl.style.marginTop = show ? "0px" : `-${headerHeight}px`;
    }, []);

    const onScroll = useCallback(() => {
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;

            const scrollEl = scrollContainerRef.current;
            const scrollTop = scrollEl ? scrollEl.scrollTop : window.scrollY;
            const atTop = scrollTop <= Math.abs(topOffset);
            const frameDelta = scrollTop - prevScroll.current;

            if (atTop) {
                prevScroll.current = scrollTop;
            } else if (Math.abs(frameDelta) < SCROLL_DIRECTION_THRESHOLD_PX) {
                return;
            } else {
                prevScroll.current = scrollTop;
            }

            const scrollingUp = frameDelta < 0;
            let nextShow = atTop || scrollingUp;

            if (scrollEl && nextShow && scrollingUp && !atTop) {
                const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;
                const fromBottom = maxScroll - scrollTop;
                if (fromBottom <= NEAR_BOTTOM_PX && frameDelta > -DELIBERATE_SCROLL_UP_AT_BOTTOM_PX) {
                    nextShow = false;
                }
            }

            const nextHidden = !nextShow;
            if (isHiddenRef.current !== nextHidden) {
                isHiddenRef.current = nextHidden;
                const headerHeight = wrapperRef.current?.offsetHeight ?? 0;
                updateContentMargin(nextShow, headerHeight);
                setHidden(nextHidden);
            }
        });
    }, [topOffset, updateContentMargin]);

    useEffect(() => {
        const target: EventTarget = scrollContainer ?? window;
        prevScroll.current = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
        onScroll();
        target.addEventListener("scroll", onScroll, false);
        return () => {
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
            target.removeEventListener("scroll", onScroll, false);
        };
    }, [onScroll, scrollContainer]);

    return (
        <Wrapper ref={wrapperRef} $hidden={hidden} data-header-hidden={hidden} aria-hidden={hidden}>
            {children}
        </Wrapper>
    );
};

export default HeaderObserver;
